import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import type { RootState, AppDispatch } from '../index'
import { API_BASE_URL } from '../../config/api'
import { setCredentials, logout } from '../slices/authSlice'

// Debug: Log baseUrl to ensure it's correct
if (import.meta.env.DEV) {
  console.log('[API] Base URL configured:', API_BASE_URL)
  if (!API_BASE_URL.startsWith('https://')) {
    console.warn('⚠️ API_BASE_URL is not using https://:', API_BASE_URL)
  }
}

const baseQuery = fetchBaseQuery({
  baseUrl: API_BASE_URL,
  prepareHeaders: (headers, { getState }) => {
    // Get token from state if available
    const state = getState() as RootState
    const token = state.auth?.token
    
    // Debug: Log token status (remove in production)
    if (import.meta.env.DEV) {
      console.log('[API] Token status:', token ? 'Present' : 'Missing', token ? `(${token.substring(0, 20)}...)` : '')
    }
    
    if (token) {
      headers.set('authorization', `Bearer ${token}`)
    } else {
      // Also try to get from localStorage as fallback
      const localToken = localStorage.getItem('token')
      if (localToken) {
        headers.set('authorization', `Bearer ${localToken}`)
        if (import.meta.env.DEV) {
          console.log('[API] Using token from localStorage')
        }
      }
    }
    return headers
  },
})

// Base query with automatic token refresh on 401
const baseQueryWithReauth = async (args: any, api: any, extraOptions: any) => {
  // Debug: Log the actual URL being called
  if (import.meta.env.DEV && args.url) {
    const fullUrl = typeof args.url === 'string' 
      ? `${API_BASE_URL}${args.url.startsWith('/') ? args.url : '/' + args.url}`
      : args.url
    console.log('[API] Calling:', fullUrl)
    if (fullUrl.startsWith('http://')) {
      console.error('❌ ERROR: API call is using http:// instead of https://:', fullUrl)
    }
  }
  
  let result = await baseQuery(args, api, extraOptions)
  
  // If we get a 401 and it's not the refresh endpoint itself, try to refresh the token
  if (result.error && result.error.status === 401 && !args.url?.includes('/auth/refresh')) {
    const state = api.getState() as RootState
    const token = state.auth?.token || localStorage.getItem('token')
    
    if (token) {
      // Try to refresh token (use the old token for refresh)
      try {
        const refreshResult = await fetch(`${API_BASE_URL}/auth/refresh`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        })
        
        if (refreshResult.ok) {
          const refreshData = await refreshResult.json()
          const { access_token } = refreshData
          
          // Update token in store
          const dispatch = api.dispatch as AppDispatch
          dispatch(setCredentials({ token: access_token, user: state.auth.user }))
          
          // Retry original request with new token
          result = await baseQuery(args, api, extraOptions)
        } else {
          // Refresh failed, logout user
          const dispatch = api.dispatch as AppDispatch
          dispatch(logout())
          // Redirect to login if not already there
          if (window.location.pathname !== '/login') {
            window.location.href = '/login'
          }
        }
      } catch (error) {
        console.error('[API] Token refresh failed:', error)
        // Refresh failed, logout user
        const dispatch = api.dispatch as AppDispatch
        dispatch(logout())
        // Redirect to login if not already there
        if (window.location.pathname !== '/login') {
          window.location.href = '/login'
        }
      }
    } else {
      // No token, logout user
      const dispatch = api.dispatch as AppDispatch
      dispatch(logout())
      // Redirect to login if not already there
      if (window.location.pathname !== '/login') {
        window.location.href = '/login'
      }
    }
  }
  
  return result
}

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Team', 'Case', 'Round', 'Memorial', 'Evidence', 'Score', 'Notification', 'User', 'Document', 'Courtroom', 'Rubric'],
  endpoints: () => ({}),
})
